import { GripVertical, Plus, Trash2, X } from 'lucide-react'
import { AutoSaveInput } from '@/components/ui/auto-save-input'
import { AutoSaveTextarea } from '@/components/ui/auto-save-textarea'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import type { ProblemFollowUp, QuestionApproach } from '../types'

interface FollowUpsSectionProps {
  followUps: ProblemFollowUp[]
  approaches: QuestionApproach[]
  onUpdate: (followUps: ProblemFollowUp[]) => void
  /** Called to save an individual follow-up, returns the new ID if created */
  onSaveFollowUp?: (followUp: ProblemFollowUp) => Promise<string | null>
}

/**
 * Get IDs of "leaf" follow-ups (those that are not prerequisites of any other follow-up).
 * Used to auto-populate prerequisites for new follow-ups.
 */
function getLeafFollowUpIds(followUps: ProblemFollowUp[]): string[] {
  const prereqIds = new Set(followUps.flatMap((f) => f.prereqs))
  return followUps.filter((f) => f.id && !prereqIds.has(f.id)).map((f) => f.id!)
}

/**
 * Detect if adding prereqId to followUpId would create a circular dependency.
 * Uses DFS to check if prereqId can reach followUpId through the prereq graph.
 */
function wouldCreateCycle(
  followUps: ProblemFollowUp[],
  followUpId: string,
  prereqId: string,
): boolean {
  const followUpMap = new Map(followUps.map((f) => [f.id, f]))

  const visited = new Set<string>()
  const stack = [prereqId]

  while (stack.length > 0) {
    const current = stack.pop()!
    if (current === followUpId) {
      return true // Found a path back to followUpId - cycle!
    }
    if (visited.has(current)) {
      continue
    }
    visited.add(current)

    const followUp = followUpMap.get(current)
    if (followUp) {
      // Check if this follow-up has prereqs that could lead back
      for (const prereq of followUp.prereqs) {
        if (!visited.has(prereq)) {
          stack.push(prereq)
        }
      }
    }
  }

  return false
}

export function FollowUpsSection({
  followUps,
  approaches,
  onUpdate,
  onSaveFollowUp,
}: FollowUpsSectionProps) {
  const handleAddFollowUp = () => {
    // Smart default: new follow-ups require all current leaf nodes
    const leafIds = getLeafFollowUpIds(followUps)
    const newFollowUp: ProblemFollowUp = {
      tempId: `temp-${crypto.randomUUID()}`,
      prompt: '',
      order: followUps.length,
      prereqs: leafIds,
      approachIds: [],
    }
    onUpdate([...followUps, newFollowUp])
  }

  const handleUpdateFollowUp = (
    index: number,
    updates: Partial<ProblemFollowUp>,
  ) => {
    const updated = [...followUps]
    updated[index] = { ...updated[index], ...updates }
    onUpdate(updated)
  }

  const handleRemoveFollowUp = (index: number) => {
    const removedId = followUps[index].id
    // Remove the follow-up and clean up any references to it in prereqs
    const updated = followUps
      .filter((_, i) => i !== index)
      .map((f, i) => ({
        ...f,
        order: i,
        // Remove the deleted follow-up from prereqs of other follow-ups
        prereqs: removedId
          ? f.prereqs.filter((id) => id !== removedId)
          : f.prereqs,
      }))
    onUpdate(updated)
  }

  const handleMoveFollowUp = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === followUps.length - 1)
    ) {
      return
    }

    const updated = [...followUps]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    ;[updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]]
    onUpdate(updated.map((f, i) => ({ ...f, order: i })))
  }

  const handleTogglePrereq = (followUpIndex: number, prereqId: string) => {
    const followUp = followUps[followUpIndex]
    const currentPrereqs = followUp.prereqs

    if (currentPrereqs.includes(prereqId)) {
      // Remove prereq
      handleUpdateFollowUp(followUpIndex, {
        prereqs: currentPrereqs.filter((id) => id !== prereqId),
      })
    } else {
      // Check for cycles before adding
      if (followUp.id && wouldCreateCycle(followUps, followUp.id, prereqId)) {
        // Don't add - would create a cycle
        return
      }
      // Add prereq
      handleUpdateFollowUp(followUpIndex, {
        prereqs: [...currentPrereqs, prereqId],
      })
    }
  }

  const handleRemovePrereq = (followUpIndex: number, prereqId: string) => {
    const followUp = followUps[followUpIndex]
    handleUpdateFollowUp(followUpIndex, {
      prereqs: followUp.prereqs.filter((id) => id !== prereqId),
    })
  }

  const handleToggleApproach = (followUpIndex: number, approachId: string) => {
    const followUp = followUps[followUpIndex]
    const currentApproachIds = followUp.approachIds

    if (currentApproachIds.includes(approachId)) {
      // Remove approach
      handleUpdateFollowUp(followUpIndex, {
        approachIds: currentApproachIds.filter((id) => id !== approachId),
      })
    } else {
      // Add approach
      handleUpdateFollowUp(followUpIndex, {
        approachIds: [...currentApproachIds, approachId],
      })
    }
  }

  const handleRemoveApproach = (followUpIndex: number, approachId: string) => {
    const followUp = followUps[followUpIndex]
    handleUpdateFollowUp(followUpIndex, {
      approachIds: followUp.approachIds.filter((id) => id !== approachId),
    })
  }

  // Helper to get a short label for a follow-up
  const getFollowUpLabel = (
    followUp: ProblemFollowUp,
    index: number,
  ): string => {
    const promptPreview =
      followUp.prompt.length > 25
        ? `${followUp.prompt.substring(0, 25)}...`
        : followUp.prompt
    return `F${index + 1}: ${promptPreview || '(empty)'}`
  }

  // Helper to get a short label for an approach
  const getApproachLabel = (approach: QuestionApproach): string => {
    const parts: string[] = []
    if (approach.name) {
      parts.push(approach.name)
    }
    if (approach.solutionType) {
      const label =
        approach.solutionType.charAt(0).toUpperCase() +
        approach.solutionType.slice(1).replace('_', ' ')
      parts.push(label)
    }
    return parts.length > 0 ? parts.join(' — ') : 'Unnamed'
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Follow-ups</h2>
          <p className="text-muted-foreground text-sm">
            Challenge users with constraint modifications after solving the base
            problem.
          </p>
        </div>
        <Button type="button" onClick={handleAddFollowUp} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add Follow-up
        </Button>
      </div>

      {followUps.length === 0 ? (
        <div className="border-border bg-muted/50 flex flex-col items-center justify-center rounded-lg border border-dashed py-12">
          <p className="text-muted-foreground mb-4">No follow-ups added yet.</p>
          <Button type="button" onClick={handleAddFollowUp} variant="outline">
            <Plus className="mr-2 h-4 w-4" />
            Add First Follow-up
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {followUps.map((followUp, index) => {
            // Get available prereqs (other follow-ups, excluding self)
            const availablePrereqs = followUps.filter(
              (f, i) => i !== index && f.id,
            )

            // Check which prereqs would create a cycle
            const wouldCycleFn = (prereqId: string) =>
              followUp.id
                ? wouldCreateCycle(followUps, followUp.id, prereqId)
                : false

            return (
              <div
                key={followUp.id ?? followUp.tempId}
                className="border-border bg-muted/30 rounded-lg border p-4"
              >
                {/* Header */}
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <GripVertical className="text-muted-foreground h-4 w-4" />
                    <span className="text-sm font-medium">
                      Follow-up {index + 1}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMoveFollowUp(index, 'up')}
                      disabled={index === 0}
                      className="h-7 w-7 p-0"
                    >
                      ↑
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMoveFollowUp(index, 'down')}
                      disabled={index === followUps.length - 1}
                      className="h-7 w-7 p-0"
                    >
                      ↓
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveFollowUp(index)}
                      className="text-destructive hover:bg-destructive/10 h-7 w-7 p-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Prompt */}
                <div className="mb-3">
                  <label className="text-muted-foreground mb-1 block text-xs font-medium">
                    Prompt
                  </label>
                  <AutoSaveTextarea
                    value={followUp.prompt}
                    onChange={(value) =>
                      handleUpdateFollowUp(index, { prompt: value })
                    }
                    onSave={async (value) => {
                      if (onSaveFollowUp) {
                        await onSaveFollowUp({ ...followUp, prompt: value })
                      }
                    }}
                    onSaveComplete={(transformedValue) => {
                      handleUpdateFollowUp(index, { prompt: transformedValue })
                    }}
                    formatOnSave
                    placeholder="Can you solve it in O(n) time? / Can you do it without extra space?"
                    className="bg-background text-sm"
                  />
                </div>

                {/* Complexity row */}
                <div className="mb-3 grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-muted-foreground mb-1 block text-xs font-medium">
                      Expected Time Complexity
                    </label>
                    <AutoSaveInput
                      type="text"
                      value={followUp.expectedTimeComplexity || ''}
                      onChange={(value) =>
                        handleUpdateFollowUp(index, {
                          expectedTimeComplexity: value || null,
                        })
                      }
                      onSave={async (value) => {
                        if (onSaveFollowUp) {
                          await onSaveFollowUp({
                            ...followUp,
                            expectedTimeComplexity: value || null,
                          })
                        }
                      }}
                      onSaveComplete={(transformedValue) => {
                        handleUpdateFollowUp(index, {
                          expectedTimeComplexity: transformedValue || null,
                        })
                      }}
                      formatOnSave
                      placeholder="O(n)"
                      className="bg-background text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-muted-foreground mb-1 block text-xs font-medium">
                      Expected Space Complexity
                    </label>
                    <AutoSaveInput
                      type="text"
                      value={followUp.expectedSpaceComplexity || ''}
                      onChange={(value) =>
                        handleUpdateFollowUp(index, {
                          expectedSpaceComplexity: value || null,
                        })
                      }
                      onSave={async (value) => {
                        if (onSaveFollowUp) {
                          await onSaveFollowUp({
                            ...followUp,
                            expectedSpaceComplexity: value || null,
                          })
                        }
                      }}
                      onSaveComplete={(transformedValue) => {
                        handleUpdateFollowUp(index, {
                          expectedSpaceComplexity: transformedValue || null,
                        })
                      }}
                      formatOnSave
                      placeholder="O(1)"
                      className="bg-background text-sm"
                    />
                  </div>
                </div>

                {/* Target Approaches */}
                <div className="mb-3">
                  <label className="text-muted-foreground mb-1 block text-xs font-medium">
                    Target Approaches
                  </label>
                  <div className="flex flex-wrap items-center gap-2">
                    {followUp.approachIds.length === 0 && (
                      <span className="text-muted-foreground text-xs">
                        (none)
                      </span>
                    )}
                    {followUp.approachIds.map((approachId) => {
                      const approach = approaches.find(
                        (a) => a.id === approachId,
                      )
                      if (!approach) return null
                      const approachIndex = approaches.findIndex(
                        (a) => a.id === approachId,
                      )
                      return (
                        <span
                          key={approachId}
                          className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-1 text-xs text-sky-800 dark:bg-sky-900 dark:text-sky-200"
                        >
                          #{approachIndex + 1}: {getApproachLabel(approach)}
                          <button
                            type="button"
                            onClick={() =>
                              handleRemoveApproach(index, approachId)
                            }
                            className="ml-0.5 rounded-full p-0.5 hover:bg-sky-200 dark:hover:bg-sky-800"
                            aria-label={`Remove approach ${approachIndex + 1}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      )
                    })}

                    {approaches.length > 0 && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-6 px-2 text-xs"
                          >
                            <Plus className="mr-1 h-3 w-3" />
                            Add
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 p-2" align="start">
                          <div className="mb-2 text-xs font-medium">
                            Select approaches:
                          </div>
                          <div className="max-h-48 space-y-1 overflow-y-auto">
                            {approaches.map((approach, approachIdx) => {
                              if (!approach.id) return null
                              const isSelected = followUp.approachIds.includes(
                                approach.id,
                              )
                              return (
                                <label
                                  key={approach.id}
                                  className="hover:bg-muted flex cursor-pointer items-center gap-2 rounded p-1 text-sm"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() =>
                                      handleToggleApproach(index, approach.id!)
                                    }
                                    className="rounded"
                                  />
                                  <span className="truncate">
                                    #{approachIdx + 1}:{' '}
                                    {getApproachLabel(approach)}
                                  </span>
                                </label>
                              )
                            })}
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                </div>

                {/* Prerequisites */}
                <div>
                  <label
                    className="text-muted-foreground mb-1 block text-xs font-medium"
                    title="This follow-up won't be shown until ALL selected follow-ups are completed (AND logic)"
                  >
                    Unlocks after
                  </label>
                  <div className="flex flex-wrap items-center gap-2">
                    {followUp.prereqs.length === 0 && (
                      <span className="text-muted-foreground text-xs">
                        (available immediately after base problem)
                      </span>
                    )}
                    {followUp.prereqs.map((prereqId) => {
                      const prereqIndex = followUps.findIndex(
                        (f) => f.id === prereqId,
                      )
                      const prereq = followUps[prereqIndex]
                      if (!prereq) return null
                      return (
                        <span
                          key={prereqId}
                          className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs text-amber-800 dark:bg-amber-900 dark:text-amber-200"
                        >
                          {getFollowUpLabel(prereq, prereqIndex)}
                          <button
                            type="button"
                            onClick={() => handleRemovePrereq(index, prereqId)}
                            className="ml-0.5 rounded-full p-0.5 hover:bg-amber-200 dark:hover:bg-amber-800"
                            aria-label={`Remove prerequisite ${prereqIndex + 1}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      )
                    })}

                    {availablePrereqs.length > 0 && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-6 px-2 text-xs"
                          >
                            <Plus className="mr-1 h-3 w-3" />
                            Add
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 p-2" align="start">
                          <div className="mb-1 text-xs font-medium">
                            Unlocks after completing:
                          </div>
                          <div className="text-muted-foreground mb-2 text-xs">
                            All selected must be done first (AND logic)
                          </div>
                          <div className="max-h-48 space-y-1 overflow-y-auto">
                            {availablePrereqs.map((prereq) => {
                              if (!prereq.id) return null
                              const prereqIdx = followUps.findIndex(
                                (f) => f.id === prereq.id,
                              )
                              const isSelected = followUp.prereqs.includes(
                                prereq.id,
                              )
                              const wouldCycle = wouldCycleFn(prereq.id)
                              return (
                                <label
                                  key={prereq.id}
                                  className={`flex items-center gap-2 rounded p-1 text-sm ${
                                    wouldCycle
                                      ? 'cursor-not-allowed opacity-50'
                                      : 'hover:bg-muted cursor-pointer'
                                  }`}
                                  title={
                                    wouldCycle
                                      ? 'Cannot add: would create a circular dependency'
                                      : undefined
                                  }
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    disabled={wouldCycle && !isSelected}
                                    onChange={() =>
                                      handleTogglePrereq(index, prereq.id!)
                                    }
                                    className="rounded"
                                  />
                                  <span className="truncate">
                                    {getFollowUpLabel(prereq, prereqIdx)}
                                  </span>
                                </label>
                              )
                            })}
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
